import * as Dialog from '@radix-ui/react-dialog'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Link, Outlet, useRouterState } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import {
  Search, Bell, ChevronDown,
  Users, Menu, Moon, Sun,
  Settings, CreditCard, Keyboard, LogOut, Globe, type LucideIcon,
} from 'lucide-react'

import { Button } from '../components/button'
import { cn } from '../components/cn'
import { QuickMemo } from './quick-memo'
import { applyTheme, currentTheme, restoreTheme, toggleTheme, type Theme } from './theme'
import { applyLang, currentLang } from '../copy'
import { notificationHandleCount } from './notification-count'
import { useActivityList, useAlerts, useHosts, useLogout, useMe, useProjects, useSearchMemos, useTeam } from './queries'
import { MobileNavProvider } from './mobile-nav'
import { AppSidebarContent, BRAND_WORDMARK, DesktopSidebar } from './sidebar'
import { t } from '../copy'
import { StaleBuildNotice } from './stale-build-notice'
import { T } from '../copy/text'
import { isActivityPath } from '../screens/activity/path'

export const PRODUCT_NAME = 'NPC 小伙伴们'
export { BRAND_WORDMARK }

/**
 * 控制台外壳：桌面端只有全局左侧栏，内容区不再保留空顶栏。
 * 搜索与任务状态归 Logo 行；通知、账户和外观设置归侧栏底部。
 * 手机端只保留打开侧栏所需的紧凑菜单按钮。
 */
export function Shell() {
  restoreTheme()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const activity = isActivityPath(pathname)
  const [navOpen, setNavOpen] = useState(false)
  const navApi = useMemo(() => ({ open: () => setNavOpen(true) }), [])
  return (
    <MobileNavProvider value={navApi}>
      <div
        data-testid="console-shell"
        data-ac={activity ? '' : undefined}
        className={activity ? 'flex min-h-screen bg-[var(--bg-0)] text-[var(--t2)]' : 'flex min-h-screen bg-m-bg0 text-m-t2'}
      >
        <DesktopSidebar headerAction={<SearchButton compact />} footer={<SidebarFooter />} />
        <div className="flex min-h-screen min-w-0 flex-1 flex-col">
          {/* 「这个页面是旧的一版」。放在最外层：旧的是整份 bundle，不是某一屏。 */}
          <StaleBuildNotice />
          {activity ? null : <MobileHeader onOpen={() => setNavOpen(true)} />}
          <MobileNavDrawer open={navOpen} onOpenChange={setNavOpen} />
          <main className="flex min-w-0 flex-1 flex-col">
            <Outlet />
          </main>
        </div>
        <QuickMemo />
        <SearchController />
      </div>
    </MobileNavProvider>
  )
}

const navigationLabel = t('ui.navigation')

function MobileNavDrawer({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50" />
        <Dialog.Content
          aria-describedby={undefined}
          data-testid="mobile-navigation"
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            document.querySelector<HTMLButtonElement>('[data-testid="mobile-navigation-trigger"]')?.focus()
          }}
          className="fixed inset-y-0 left-0 z-50 flex w-[min(318px,calc(100vw-48px))] flex-col overflow-hidden border-r border-m-line1 bg-m-bg1 text-m-t2 shadow-[var(--m-pop)]"
        >
          <Dialog.Title className="sr-only">{navigationLabel}</Dialog.Title>
          <AppSidebarContent
            mode="mobile"
            onNavigate={() => onOpenChange(false)}
            headerAction={<SearchButton />}
            footer={<DrawerAccount onNavigate={() => onOpenChange(false)} />}
          />
          <Dialog.Close asChild>
            <Button
              variant="ghost"
              size="sm"
              aria-label={t('ui.close')}
              className="absolute right-[8px] top-[8px] size-[34px] p-0 text-m-t1"
            >
              <span aria-hidden="true">×</span>
            </Button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// ---------------------------------------------------------------- 搜索

/**
 * ⌘K 搜一切（R3，PRD FR-3 / 研讨会裁决二）。
 *
 * 输入后分四组显示：项目 / 机器 / 备忘录正文 / 动态。
 * 每组最多 5 条 + 「查看全部」；搜不到就如实说。
 * 项目与机器是本地过滤已加载列表；备忘录走 /memos/search；
 * 动态走 /timeline 的本地过滤（后端全文搜索等有量了再接）。
 */
function SearchButton({ compact = false }: { compact?: boolean }) {
  return (
    <button
      type="button"
      data-testid="search-everything-trigger"
      aria-label={t('nav.search.hint.all')}
      title={`${t('nav.search.hint.all')} (⌘K)`}
      onClick={() => document.dispatchEvent(new CustomEvent('npc-search-open'))}
      className={cn(
        'flex size-[34px] cursor-pointer items-center justify-center rounded-npc-chip border-0 bg-transparent text-m-t3 transition-colors hover:bg-m-line1 hover:text-m-t1',
        compact && 'max-[1119px]:size-[28px]',
      )}
    >
      <Search size={15} aria-hidden="true" />
    </button>
  )
}

function SearchController() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  // 全局 ⌘K 开关
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey) && !event.isComposing) {
        event.preventDefault()
        setOpen((v) => !v)
      }
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    const openSearch = () => setOpen(true)
    document.addEventListener('npc-search-open', openSearch)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('npc-search-open', openSearch)
    }
  }, [])

  const q = query.trim().toLowerCase()
  const hasQuery = q.length > 0

  // 项目与机器：本地过滤
  const projects = useProjects()
  const hosts = useHosts()
  const projectHits = (projects.data?.items ?? [])
    .filter((p) => p.name.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q))
    .slice(0, 5)
  const hostHits = (hosts.data?.items ?? [])
    .filter((h) => (h.name ?? '').toLowerCase().includes(q))
    .slice(0, 5)

  // 备忘录正文：跨项目搜索端点
  const memoSearch = useSearchMemos(query.trim(), hasQuery)
  const memoHits = (memoSearch.data?.items ?? []).slice(0, 5)

  // 动态：本地过滤（时间线没有搜索端点，量小先全取）
  const timeline = useActivityList({ limit: 20 })
  const timelineHits = (timeline.data?.items ?? [])
    .filter((item) => JSON.stringify(item).toLowerCase().includes(q))
    .slice(0, 5)

  const groups: Array<{
    key: string
    label: string
    items: Array<{ id: string; label: string; href: string }>
    more?: string
  }> = hasQuery
    ? [
        {
          key: 'projects',
          label: t('nav.search.group.projects'),
          items: projectHits.map((p) => ({ id: p.id, label: p.name, href: `/projects/${p.id}` })),
        },
        {
          key: 'hosts',
          label: t('nav.search.group.hosts'),
          items: hostHits.map((h) => ({ id: h.id, label: h.name ?? h.id, href: `/hosts/${h.id}` })),
        },
        {
          key: 'memos',
          label: t('nav.search.group.memos'),
          items: memoHits.map((m) => ({
            id: m.id,
            label: m.body ? m.body.slice(0, 50) : '(语音)',
            href: '/memos',
          })),
        },
        {
          key: 'timeline',
          label: t('nav.search.group.timeline'),
          items: timelineHits.map((item, i) => ({
            id: `${item.occurred_at}-${i}`,
            label: item.event_type ?? '',
            href: '/timeline',
          })),
        },
      ].filter((g) => g.items.length > 0)
    : []

  return open ? (
        <Dialog.Root open onOpenChange={(next) => (!next ? setOpen(false) : undefined)}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-[70] bg-npc-overlay" />
            <Dialog.Content
              aria-describedby={undefined}
              data-testid="search-everything"
              className="fixed left-1/2 top-[120px] z-[80] w-[min(560px,calc(100vw-32px))] -translate-x-1/2 rounded-npc-card-sm border border-m-line2 bg-m-bg1 shadow-[var(--m-pop)]"
            >
              <Dialog.Title className="sr-only">{t('nav.search.everything')}</Dialog.Title>

            {/* 输入行 */}
            <div className="flex items-center gap-[8px] border-b border-m-line1 px-[14px] py-[12px]">
              <Search size={16} className="shrink-0 text-m-t4" />
              <input
                type="text"
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('nav.search.hint.all')}
                aria-label={t('nav.search.hint.all')}
                data-testid="search-everything-input"
                className="w-full border-none bg-transparent p-0 text-npc-body-xl text-m-t1 outline-none placeholder:text-m-t4"
              />
            </div>

            {/* 结果组 */}
            <div className="max-h-[400px] overflow-y-auto px-[6px] py-[6px]">
              {!hasQuery ? (
                <p className="px-[8px] py-[6px] text-npc-caption text-m-t4">
                  {t('nav.search.hint.all')}
                </p>
              ) : groups.length === 0 ? (
                <p className="px-[8px] py-[6px] text-npc-caption text-m-t4" data-testid="search-none">
                  {t('nav.search.none')}
                </p>
              ) : (
                groups.map((g) => (
                  <div key={g.key} className="mb-[6px]">
                    <div className="px-[8px] py-[4px] text-npc-meta font-semibold tracking-[0.05em] text-m-t4">
                      {g.label}
                    </div>
                    {g.items.map((item) => (
                      <Dialog.Close asChild key={item.id}>
                        <Link
                          to={item.href as never}
                          className="flex h-[34px] items-center gap-[8px] rounded-npc-tag px-[8px] text-npc-body text-m-t2 hover:bg-m-line1 hover:text-m-t1"
                        >
                          <span className="min-w-0 flex-1 truncate">{item.label}</span>
                        </Link>
                      </Dialog.Close>
                    ))}
                  </div>
                ))
              )}
            </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
  ) : null
}

// ---------------------------------------------------------------- 通知

function NotificationButton({ sidebar = false }: { sidebar?: boolean }) {
  const alerts = useAlerts('open')
  const activity = useActivityList({ view: 'action' })
  const openAlerts = alerts.data?.items ?? []
  const count = notificationHandleCount(activity.data?.requires_action_count ?? 0, openAlerts.length)

  return (
    <Link
      to={'/activity?view=action&alerts=1' as never}
      aria-label={t('nav.notification')}
      title={t('nav.notification')}
      data-testid="notification-bell"
      className={cn(
        'relative flex cursor-pointer items-center justify-center rounded-npc-tag',
        'text-m-t3 transition-colors hover:bg-m-line1 hover:text-m-t1',
        sidebar ? 'size-[36px] shrink-0' : 'size-[30px]',
      )}
    >
      <Bell size={15} strokeWidth={2} />
      {count > 0 ? (
        <span
          data-testid="notification-badge"
          className="absolute -right-[1px] -top-[1px] flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-m-bad px-[4px] text-npc-meta-sm font-semibold leading-none text-white"
        >
          {count > 99 ? '99+' : String(count)}
        </span>
      ) : null}
    </Link>
  )
}

function SidebarFooter() {
  return (
    <div
      data-testid="sidebar-utility-footer"
      className="flex w-full shrink-0 items-center gap-[2px] border-t border-m-line1 p-[8px] max-[1119px]:flex-col max-[1119px]:p-[5px]"
    >
      <AccountMenu />
      <NotificationButton sidebar />
    </div>
  )
}

// ---------------------------------------------------------------- 账户菜单

/**
 * 侧栏账户菜单（导航冻结参考 NavBar.dc.html，2026-08-26 纠偏）。
 *
 * 桌面：入口固定在侧栏底部；菜单向右展开，沿用紧凑 274px 信息架构。
 * 只有五项：设置 / 成员与权限（人数）/ 账单与额度（额度未接不编数字）/
 * 键盘快捷键（⌘/）/ 退出登录（危险色）。
 *
 * **不得**塞业务模块（安全体检/服务商/模型/AI 接口/告警）——它们留在这
 * 些页面自己的入口（设置页的「设置类」卡），路由一个不删。
 * 桌面不做「换主题」分区：外观归设置页（SPEC：外观入口按设计归设置）。
 */
function AccountMenu() {
  const me = useMe()
  const logout = useLogout()
  const team = useTeam()
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const email = me.data?.email ?? ''
  const displayName = me.data?.display_name || email.split('@')[0] || t('nav.account.me')
  const initial = displayName.charAt(0).toUpperCase() || 'N'
  const memberCount = team.data?.items?.length ?? 0
  // 外观三态（冻结参考：浅色/深色/跟随系统）。「跟随系统」= 移除本机选择，
  // 每次进入按 matchMedia 判定；切回浅/深则写入本机。
  const themeMode = (() => {
    try {
      const saved = window.localStorage.getItem('npc-theme')
      if (saved === 'light' || saved === 'dark') return saved
    } catch {
      // 无 localStorage 视为跟随系统
    }
    return 'system'
  })()
  const [, rerender] = useState(0)
  function applyThemeMode(mode: 'light' | 'dark' | 'system') {
    try {
      if (mode === 'system') window.localStorage.removeItem('npc-theme')
      else window.localStorage.setItem('npc-theme', mode)
    } catch {
      // 写不进就只切换当前视图
    }
    if (mode === 'system') restoreTheme()
    else applyTheme(mode)
    rerender((n) => n + 1)
  }

  // 菜单头第三行（冻结参考）：「{登录方式} · 上次 {时间}」。
  // sign_in_paths 有哪些可用方式；上次登录时间 me 契约没有——没有就不显示，不编时间。
  const sessionLine = (() => {
    const paths = me.data?.sign_in_paths?.paths ?? []
    const available = paths.filter((p) => p.available).map((p) => p.kind)
    if (available.length === 0) return null
    const kind = available.includes('password') ? t('nav.account.session.password') : (available[0] ?? '')
    return t('nav.account.session', { method: kind, time: '—' })
  })()

  // 全局 ⌘/ 开快捷键面板
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === '/' && (event.metaKey || event.ctrlKey) && !event.isComposing) {
        event.preventDefault()
        setShortcutsOpen(true)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
    }
  })

  const items: Array<{
    key: string
    label: string
    icon: LucideIcon
    hint?: string
    danger?: boolean
    onSelect: () => void
    href?: string
  }> = [
    {
      key: 'settings',
      label: t('nav.account.settings'),
      icon: Settings,
      href: '/account',
      onSelect: () => {},
    },
    {
      key: 'team',
      label: t('nav.account.team'),
      icon: Users,
      hint: memberCount > 0 ? t('nav.account.team.hint', { n: String(memberCount) }) : undefined,
      href: '/team',
      onSelect: () => {},
    },
    {
      key: 'billing',
      label: t('nav.account.billing'),
      icon: CreditCard,
      // 额度数据源还没接——不编「剩 62%」这种数字，接上之前不显示 hint
      href: '/account',
      onSelect: () => {},
    },
    {
      key: 'shortcuts',
      label: t('nav.account.shortcuts'),
      icon: Keyboard,
      hint: t('nav.account.shortcuts.hint'),
      onSelect: () => setShortcutsOpen(true),
    },
    {
      key: 'logout',
      label: t('nav.account.logout'),
      icon: LogOut,
      danger: true,
      onSelect: () => logout.mutate(),
    },
  ]

  return (
    <div className="min-w-0 flex-1 max-[1119px]:flex-none">
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            data-testid="account-menu-trigger"
            aria-label={t('nav.account.me')}
            title={t('nav.account.me')}
            className="flex h-[42px] w-full min-w-0 cursor-pointer items-center gap-[9px] overflow-hidden rounded-npc-tag px-[8px] text-left transition-colors hover:bg-m-bg2 max-[1119px]:justify-center max-[1119px]:px-0"
          >
            <span
              className="flex size-[28px] shrink-0 items-center justify-center rounded-full border border-m-line3 bg-m-bg4 text-npc-meta font-semibold text-m-t1"
            >
              {initial}
            </span>
            <span className="min-w-0 flex-1 truncate text-npc-body text-m-t1 max-[1119px]:hidden">{displayName}</span>
            <Settings size={14} className="shrink-0 text-m-t4 max-[1119px]:hidden" aria-hidden="true" />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            side="right"
            sideOffset={8}
            align="end"
            data-testid="account-menu"
            className={cn(
              'z-50 w-[274px] overflow-hidden rounded-[8px]',  /* npc-token-allow: 导航冻结参考 NavBar.dc 原值，整站 token 无此档 */
              'border border-m-line2 bg-m-bg1 shadow-[var(--m-pop)]',
            )}
          >
            {/* 菜单头：仅名称与邮箱（冻结参考的 34px 头像版式） */}
            <div className="flex items-center gap-[10px] border-b border-m-line1 px-[13px] py-[11px]">
              <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-m-bg4 text-npc-body font-semibold text-m-t1 shadow-[inset_0_0_0_1px_var(--m-line2)]"> {/* npc-token-allow: 导航冻结参考 NavBar.dc 原值，整站 token 无此档 */}
                {initial}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[12.5px] font-semibold text-m-t1"> {/* npc-token-allow: 导航冻结参考 NavBar.dc 原值，整站 token 无此档 */}
                  {displayName}
                </div>
                <div className="truncate font-npc-mono text-npc-meta text-m-t3"> {/* npc-token-allow: 导航冻结参考 NavBar.dc 原值，整站 token 无此档 */}
                  {email}
                </div>
                {sessionLine ? (
                  <div className="mt-[3px] truncate text-[10.5px] text-m-t4"> {/* npc-token-allow: 导航冻结参考 NavBar.dc 原值，整站 token 无此档 */}
                    {sessionLine}
                  </div>
                ) : null}
              </div>
            </div>

            {/* 外观/语言（冻结参考 acctToggles）：分段控件行 */}
            <div className="py-[4px]">
              {[
                {
                  key: 'appearance',
                  label: t('nav.account.appearance'),
                  icon: Sun,
                  options: [
                    { key: 'light', label: t('nav.theme.light'), on: themeMode === 'light' },
                    { key: 'dark', label: t('nav.theme.dark'), on: themeMode === 'dark' },
                    { key: 'system', label: t('nav.account.appearance.follow'), on: themeMode === 'system' },
                  ] as Array<{ key: string; label: string; on: boolean }>,
                  onPick: (k: string) => applyThemeMode(k as 'light' | 'dark' | 'system'),
                },
                {
                  key: 'lang',
                  label: t('nav.account.lang'),
                  icon: Globe,
                  options: [
                    { key: 'zh', label: '简体中文', on: currentLang() === 'zh' },
                    { key: 'en', label: 'English', on: currentLang() === 'en' },
                  ] as Array<{ key: string; label: string; on: boolean }>,
                  onPick: (k: string) => {
                    if (k === 'zh' || k === 'en') applyLang(k)
                  },
                },
              ].map((tg) => {
                const Icon = tg.icon
                return (
                  <div
                    key={tg.key}
                    className="flex h-[38px] items-center gap-[9px] border-b border-m-line1 px-[13px]"  /* npc-token-allow: 导航冻结参考 NavBar.dc 原值，整站 token 无此档 */
                    data-testid={`account-toggle-${tg.key}`}
                  >
                    <span className="flex h-[16px] w-[16px] shrink-0 items-center justify-center text-m-t3"> {/* npc-token-allow: 导航冻结参考 NavBar.dc 原值，整站 token 无此档 */}
                      <Icon size={14} strokeWidth={1.9} aria-hidden />
                    </span>
                    <span className="shrink-0 text-npc-caption-sm text-m-t2"> {/* npc-token-allow: 导航冻结参考 NavBar.dc 原值，整站 token 无此档 */}
                      {tg.label}
                    </span>
                    <div className="flex min-w-0 flex-1 justify-end gap-[2px] rounded-[7px] bg-m-bg3 p-[2px]"> {/* npc-token-allow: 导航冻结参考 NavBar.dc 原值，整站 token 无此档 */}
                      {'options' in tg
                        ? tg.options.map((o) => {
                            const on = Boolean(o.on)
                            return (
                              <button
                                key={o.key}
                                type="button"
                                disabled={!('onPick' in tg)}
                                onClick={'onPick' in tg ? () => tg.onPick?.(o.key) : undefined}
                                data-testid={`account-toggle-${tg.key}-${o.key}`}
                                className={cn(
                                  'cursor-pointer rounded-npc-chip-inline border-none px-[8px] py-[2px] font-[inherit] text-npc-meta',
                                  on
                                    ? 'bg-m-pri font-semibold text-m-pri-t'
                                    : 'bg-transparent text-m-t3 hover:text-m-t2',
                                  !('onPick' in tg) && 'cursor-default',
                                )}
                              >
                                {o.label}
                              </button>
                            )
                          })
                        : null}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* 五项（冻结参考 acctItems）：34px 行、14px 图标、12px 文字、右侧 mono hint */}
            <div className="py-[4px]">
              {items.map((item) => {
                const Icon = item.icon
                const inner = (
                  <>
                    <Icon size={14} strokeWidth={1.9} aria-hidden className="shrink-0" />
                    <span className="min-w-0 flex-1 truncate text-[12px]"> {/* npc-token-allow: 导航冻结参考 NavBar.dc 原值，整站 token 无此档 */}
                      {item.label}
                    </span>
                    {item.hint ? (
                      <span className="shrink-0 font-npc-mono text-[10.5px] text-m-t4"> {/* npc-token-allow: 导航冻结参考 NavBar.dc 原值，整站 token 无此档 */}
                        {item.hint}
                      </span>
                    ) : null}
                  </>
                )
                const rowClass = cn(
                  // 字号 12px 钉在行上（DropdownMenu.Item 会继承父级 13px）
                  'flex h-[34px] cursor-pointer items-center gap-[9px] px-[13px] text-[12px] outline-none',  /* npc-token-allow: 导航冻结参考 NavBar.dc 原值，整站 token 无此档 */
                  item.danger
                    ? 'text-m-bad-t data-[highlighted]:bg-m-bad-s'
                    : 'text-m-t2 data-[highlighted]:bg-m-line1 data-[highlighted]:text-m-t1',
                )
                return item.href ? (
                  <DropdownMenu.Item key={item.key} asChild>
                    <Link to={item.href} data-testid={`account-item-${item.key}`} className={rowClass}>
                      {inner}
                    </Link>
                  </DropdownMenu.Item>
                ) : (
                  <DropdownMenu.Item key={item.key} onSelect={item.onSelect} data-testid={`account-item-${item.key}`} className={rowClass}>
                    {inner}
                  </DropdownMenu.Item>
                )
              })}
            </div>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      {/* 键盘快捷键面板：菜单项与全局 ⌘/ 都能开 */}
      <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </div>
  )
}

/**
 * 键盘快捷键面板（账户菜单第 4 项，⌘/ 全局开关）。
 * 列的是**真实存在**的全局快捷键——没有的 不写。
 */
function ShortcutsDialog(props: { open: boolean; onClose: () => void }) {
  // v3 §4C.4：分「全局」「备忘录」两组
  const groups: Array<{ label: string; rows: Array<[string, string]> }> = [
    {
      label: t('nav.shortcuts.group.global'),
      rows: [
        [t('nav.search.hint'), '⌘K'],
        [t('nav.account.shortcuts'), '⌘/'],
        [t('ui.close'), 'Esc'],
      ],
    },
    {
      label: t('nav.shortcuts.group.memo'),
      rows: [[t('memo.quick.badge'), 'N']],
    },
  ]
  if (!props.open) return null
  return (
    <Dialog.Root open onOpenChange={(next) => (!next ? props.onClose() : undefined)}>
      <Dialog.Overlay className="fixed inset-0 z-[70] bg-npc-overlay" /> {/* npc-token-allow: 导航冻结参考 NavBar.dc 原值，整站 token 无此档 */}
        <Dialog.Content
          aria-describedby={undefined}
          data-testid="shortcuts-dialog"
          className="fixed left-1/2 top-1/2 z-50 w-[min(400px,calc(100vw-48px))] -translate-x-1/2 -translate-y-1/2 rounded-[12px] border border-m-line2 bg-m-bg1 p-[18px] shadow-[var(--m-pop)]" /* npc-token-allow: 导航冻结参考 NavBar.dc 原值，整站 token 无此档 */
        >
          <Dialog.Title className="m-0 text-[15px] font-semibold text-m-t1"> {/* npc-token-allow: 导航冻结参考 NavBar.dc 原值，整站 token 无此档 */}
            {t('nav.account.shortcuts')}
          </Dialog.Title>
          <div className="mt-[12px]">
            {groups.map((g) => (
              <div key={g.label} className="mb-[10px]">
                <div className="mb-[4px] text-npc-meta font-semibold tracking-[0.05em] text-m-t4"> {/* npc-token-allow: v3 §4C.4 */ }
                  {g.label}
                </div>
                <ul className="m-0 list-none p-0">
                  {g.rows.map(([label, keys]) => (
                    <li key={label} className="flex h-[34px] items-center justify-between gap-[12px] text-npc-body text-m-t2"> {/* npc-token-allow: v3 §4C.4 */ }
                      <span>{label}</span>
                      <kbd className="rounded-npc-chip-inline border border-m-line2 bg-m-bg2 px-[7px] py-[2px] font-npc-mono text-npc-caption-sm not-italic text-m-t2"> {/* npc-token-allow: v3 §4C.4 */ }
                        {keys}
                      </kbd>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="mb-0 mt-[4px] text-npc-caption-sm text-m-t4"> {/* npc-token-allow: v3 §4C.4 */ }
            {t('nav.shortcuts.yield.note')}
          </p>
          <Dialog.Close
            className="mt-[14px] h-[32px] cursor-pointer rounded-[8px] border border-m-line2 bg-m-bg1 px-[14px] font-[inherit] text-npc-body text-m-t2 hover:text-m-t1" /* npc-token-allow: 导航冻结参考 NavBar.dc 原值，整站 token 无此档 */
            aria-label={t('ui.close')}
          >
            <T id="ui.close" />
          </Dialog.Close>
        </Dialog.Content>
    </Dialog.Root>
  )
}

// ---------------------------------------------------------------- 移动端

function MobileHeader({ onOpen }: { onOpen: () => void }) {
  return (
    <header
      data-testid="mobile-header"
      className={cn(
        'sticky top-0 z-30 h-[44px] w-full shrink-0 items-center justify-start',
        'border-b border-m-bar-border bg-m-bar backdrop-blur-[12px] px-[12px]',
        'max-sm:flex min-[601px]:hidden',
      )}
    >
      <Button
        data-testid="mobile-navigation-trigger"
        variant="ghost"
        size="sm"
        aria-label={navigationLabel}
        onClick={onOpen}
        className="size-[40px] p-0 text-m-t1"
      >
        <Menu size={20} aria-hidden="true" />
      </Button>

      <span className="sr-only">{BRAND_WORDMARK}</span>
      <span className="flex-1" />
      <NotificationButton />
    </header>
  )
}

/**
 * 移动端账户入口（冻结参考 MobileNav.dc）：**不复用桌面下拉**，
 * 点头像弹 bottom sheet——偏好（外观/语言）与账号（设置/成员与权限/
 * 账单与额度/退出登录）两组。
 */
function DrawerAccount({ onNavigate }: { onNavigate: () => void }) {
  const [sheetOpen, setSheetOpen] = useState(false)
  const me = useMe()
  const email = me.data?.email ?? ''
  const displayName = me.data?.display_name ?? email.split('@')[0] ?? ''
  return (
    <div className="mt-auto border-t border-m-line1 px-[14px] pt-[12px]">
      <button
        type="button"
        data-testid="mobile-account-trigger"
        onClick={() => setSheetOpen(true)}
        className="flex h-[44px] w-full cursor-pointer items-center gap-[10px] border-none bg-transparent p-0 text-left"
      >
        <div className="flex size-[30px] items-center justify-center rounded-full bg-m-bg4 text-[12px] font-semibold text-m-t1"> {/* npc-token-allow: 导航冻结参考 MobileNav.dc 原值，整站 token 无此档 */}
          {displayName.charAt(0).toUpperCase() || 'N'}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-npc-body text-m-t1">{displayName}</div> {/* npc-token-allow: 导航冻结参考 MobileNav.dc 原值，整站 token 无此档 */}
          <div className="truncate text-npc-meta text-m-t4">{email}</div> {/* npc-token-allow: 导航冻结参考 MobileNav.dc 原值，整站 token 无此档 */}
        </div>
        <ChevronDown size={13} className="text-m-t4" />
      </button>
      <MobileAccountSheet open={sheetOpen} onClose={() => setSheetOpen(false)} onNavigate={onNavigate} />
    </div>
  )
}

/** 移动端账户 bottom sheet：偏好 + 账号 两组（MobileNav 冻结参考）。 */
function MobileAccountSheet(props: { open: boolean; onClose: () => void; onNavigate: () => void }) {
  const logout = useLogout()
  const team = useTeam()
  const [theme, setTheme] = useState<Theme>(currentTheme())
  const memberCount = team.data?.items?.length ?? 0


  const row = 'flex h-[46px] items-center gap-[10px] px-[14px] text-[14px] text-m-t2'  /* npc-token-allow: 导航冻结参考 MobileNav.dc 原值，整站 token 无此档 */

  return (
    <Dialog.Root open={props.open} onOpenChange={(next) => (!next ? props.onClose() : undefined)}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content
          aria-describedby={undefined}
          data-testid="mobile-account-sheet"
          className="fixed inset-x-0 bottom-0 z-50 rounded-t-[14px] border-t border-m-line2 bg-m-bg1 pb-[14px] pt-[10px] shadow-[var(--m-pop)]"  /* npc-token-allow: 导航冻结参考 MobileNav.dc 原值，整站 token 无此档 */
        >
          <Dialog.Title className="sr-only">{t('nav.account.me')}</Dialog.Title>
          <div className="mx-auto mb-[8px] h-[4px] w-[36px] rounded-full bg-m-line3" aria-hidden /> {/* npc-token-allow: 导航冻结参考 MobileNav.dc 原值，整站 token 无此档 */}

          {/* 偏好：外观、语言 */}
          <div className="px-[14px] pb-[6px] pt-[4px] text-npc-meta font-semibold uppercase tracking-[0.05em] text-m-t4"> {/* npc-token-allow: 导航冻结参考 MobileNav.dc 原值，整站 token 无此档 */}
            <T id="nav.account.mobile.prefs" />
          </div>
          <div className="flex items-center justify-between gap-[10px] px-[14px]">
            <span className="flex h-[46px] flex-1 items-center gap-[10px] text-[14px] text-m-t2"> {/* npc-token-allow: 导航冻结参考 MobileNav.dc 原值，整站 token 无此档 */}
              <Moon size={15} aria-hidden className="shrink-0 text-m-t3" />
              <T id="nav.theme.switch" />
            </span>
            <button
              type="button"
              data-testid="theme-toggle-mobile"
              onClick={() => setTheme(toggleTheme())}
              aria-label={t('nav.theme.switch')}
              className="flex h-[32px] shrink-0 cursor-pointer items-center gap-[4px] rounded-[7px] border border-m-line2 bg-m-bg2 px-[10px] text-[12px] text-m-t2"  /* npc-token-allow: 导航冻结参考 MobileNav.dc 原值，整站 token 无此档 */
            >
              {theme === 'dark' ? <Moon size={12} aria-hidden /> : <Sun size={12} aria-hidden />}
              <T id={theme === 'dark' ? 'nav.theme.dark' : 'nav.theme.light'} />
            </button>
          </div>
          <div className="flex items-center justify-between gap-[10px] px-[14px]">
            <span className="flex h-[46px] flex-1 items-center gap-[10px] text-[14px] text-m-t2"> {/* npc-token-allow: 导航冻结参考 MobileNav.dc 原值，整站 token 无此档 */}
              <span aria-hidden className="w-[15px] text-center text-npc-body text-m-t3">文</span> {/* npc-token-allow: 导航冻结参考 NavBar.dc/MobileNav.dc 原值，整站 token 无此档 */}
              <T id="nav.account.mobile.lang" />
            </span>
            <span className="flex shrink-0 gap-[2px] rounded-[7px] bg-m-bg3 p-[2px]"> {/* npc-token-allow: 导航冻结参考 MobileNav.dc 原值，整站 token 无此档 */}
              {(['zh', 'en'] as const).map((l) => (
                <button
                  key={l}
                  type="button"
                  data-testid={`mobile-lang-${l}`}
                  onClick={() => applyLang(l)}
                  className={cn(
                    'cursor-pointer rounded-npc-chip-inline border-none px-[8px] py-[2px] font-[inherit] text-npc-caption-sm',
                    currentLang() === l
                      ? 'bg-m-pri font-semibold text-m-pri-t'
                      : 'bg-transparent text-m-t3',
                  )}
                >
                  {l === 'zh' ? '简体中文' : 'English'}
                </button>
              ))}
            </span>
          </div>

          {/* 账号：设置、成员与权限、账单与额度、退出登录 */}
          <div className="mt-[6px] border-t border-m-line1 px-[14px] pb-[6px] pt-[8px] text-npc-meta font-semibold uppercase tracking-[0.05em] text-m-t4"> {/* npc-token-allow: 导航冻结参考 MobileNav.dc 原值，整站 token 无此档 */}
            <T id="nav.account.mobile.account" />
          </div>
          <div className="px-[6px]">
            <Dialog.Close asChild>
              <Link to="/account" data-testid="mobile-account-settings" className={row}>
                <Settings size={15} aria-hidden className="shrink-0 text-m-t3" />
                <T id="nav.account.settings" />
              </Link>
            </Dialog.Close>
            <Dialog.Close asChild>
              <Link to="/team" data-testid="mobile-account-team" className={row}>
                <Users size={15} aria-hidden className="shrink-0 text-m-t3" />
                <span className="flex-1">
                  <T id="nav.account.team" />
                </span>
                {memberCount > 0 ? (
                  <span className="shrink-0 font-npc-mono text-npc-meta text-m-t4"> {/* npc-token-allow: 导航冻结参考 MobileNav.dc 原值，整站 token 无此档 */}
                    {t('nav.account.team.hint', { n: String(memberCount) })}
                  </span>
                ) : null}
              </Link>
            </Dialog.Close>
            <Dialog.Close asChild>
              <Link to="/account" data-testid="mobile-account-billing" className={row}>
                <CreditCard size={15} aria-hidden className="shrink-0 text-m-t3" />
                <T id="nav.account.billing" />
              </Link>
            </Dialog.Close>
            <button
              type="button"
              data-testid="nav-logout-mobile"
              disabled={logout.isPending}
              onClick={() => logout.mutate()}
              className="flex h-[46px] w-full cursor-pointer items-center gap-[10px] border-none bg-transparent px-[14px] text-left text-[14px] text-m-bad disabled:opacity-50"  /* npc-token-allow: 导航冻结参考 MobileNav.dc 原值，整站 token 无此档 */
            >
              <LogOut size={15} aria-hidden className="shrink-0 text-m-bad-t" />
              <T id="nav.account.logout" />
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
